<template>
    <div style="max-width: 600px; margin: auto; text-align: left;">
        <FieldHeader class="q-mb-xs" v-if="showHeader" :field="field" />

        <div v-if="emojiLabel" class="text-h5 text-center q-mb-sm">{{ emojiLabel }}</div>

        <q-slider
            :model-value="sliderValue"
            :min="sliderMin"
            :max="sliderMax"
            :step="sliderStep"
            :readonly="readOnly"
            label
            snap
            markers
            @update:model-value="onUpdate"
            @change="$emit('blur')"
        />

        <div class="row justify-between text-caption text-grey-7 q-mt-xs">
            <div>{{ startLabel }}</div>
            <span :class="modelValue === null || modelValue === '' ? 'text-grey-6' : 'text-grey-8 text-h5'">
                {{ displayValue }}
            </span>
            <div>{{ endLabel }}</div>
        </div>
        
    </div>
</template>

<script>
    import FieldHeader from './FieldHeader.vue';

    export default {
        name: 'ScaleField',
        components: { FieldHeader },
        props: {
            field: {
                type: Object,
                required: true,
            },
            modelValue: {
                type: [Number, String, null],
                default: null,
            },
            readOnly: {
                type: Boolean,
                default: false,
            },
            showHeader: {
                type: Boolean,
                default: true,
            },
        },
        emits: ['update:modelValue', 'blur'],
        mounted() {
            this.initializeDefaultValue();
        },
        watch: {
            field: {
                handler() {
                    this.initializeDefaultValue();
                },
                deep: true,
            },
            readOnly() {
                this.initializeDefaultValue();
            },
        },
        computed: {
            sliderMin() {
                const value = Number(this.field?.options?.min);
                return Number.isFinite(value) ? value : 1;
            },
            sliderMax() {
                const value = Number(this.field?.options?.max);
                if (!Number.isFinite(value)) return 10;
                return value < this.sliderMin ? this.sliderMin : value;
            },
            sliderStep() {
                const value = Number(this.field?.options?.step);
                return Number.isFinite(value) && value > 0 ? value : 1;
            },
            // Calculate default slider value as the midpoint between min and max, snapped to the nearest step
            defaultSliderValue() {
                const midpoint = Math.floor((this.sliderMin + this.sliderMax) / 2);
                const stepsFromMin = (midpoint - this.sliderMin) / this.sliderStep;
                const snapped = this.sliderMin + Math.round(stepsFromMin) * this.sliderStep;
                const candidate = Math.min(this.sliderMax, Math.max(this.sliderMin, snapped));

                const precision = this.getMaxPrecision([this.sliderMin, this.sliderMax, this.sliderStep]);
                return Number(candidate.toFixed(precision));
            },
            sliderValue() {
                const value = Number(this.modelValue);
                if (this.modelValue !== null && this.modelValue !== '' && Number.isFinite(value)) {
                    return value;
                }
                return this.defaultSliderValue;
            },
            startLabel() {
                return this.field?.options?.startLabel || this.sliderMin;
            },
            endLabel() {
                return this.field?.options?.endLabel || this.sliderMax;
            },
            displayValue() {
                if (this.modelValue === null || this.modelValue === '') {
                    return '';
                }
                return `${this.modelValue}`;
            },
            emojiLabel() {
                const emoji = this.field?.options?.emoji;
                if (!emoji) return null;

                const emojiMap = {
                    happy: '😁 Happy',
                    excited: '😜 Excited',
                    angry: '😠 Angry',
                    sad: '😢 Sad',
                    worried: '😟 Worried',
                    overwhelmed: '😫 Overwhelmed',
                };

                const key = String(emoji).toLowerCase();
                return emojiMap[key] || emoji;
            },
        },
        methods: {
            getMaxPrecision(values) {
                return values.reduce((maxPrecision, value) => {
                    const valueString = String(value);
                    if (valueString.includes('e-')) {
                        const exponent = Number(valueString.split('e-')[1]);
                        return Math.max(maxPrecision, Number.isFinite(exponent) ? exponent : 0);
                    }

                    const decimalPart = valueString.split('.')[1];
                    return Math.max(maxPrecision, decimalPart ? decimalPart.length : 0);
                }, 0);
            },
            initializeDefaultValue() {
                if (this.readOnly) return;
                if (this.modelValue !== null && this.modelValue !== '') return;
                this.$emit('update:modelValue', this.defaultSliderValue);
            },
            onUpdate(value) {
                this.$emit('update:modelValue', value);
            },
            reset() {
                this.$emit('update:modelValue', null);
                this.$emit('blur');
            },
        },
    };
</script>